
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface EnhancedAnalysisRequest {
  content: string;
  platform: string;
  author?: string;
  hashtags?: string[];
  metadata?: any;
  options?: {
    providers?: ('openai' | 'anthropic' | 'huggingface' | 'local')[];
    includeEmotions?: boolean;
    includeIntents?: boolean;
    includeEntities?: boolean;
    deepAnalysis?: boolean;
  };
}

interface EnhancedAnalysisResult {
  shouldFilter: boolean;
  reasons: string[];
  confidence: number;
  sentiment?: 'positive' | 'negative' | 'neutral';
  providers?: {
    openai?: any;
    anthropic?: any;
    huggingface?: any;
    local?: any;
  };
  emotions?: {
    joy: number;
    anger: number;
    fear: number;
    sadness: number;
    surprise: number;
    disgust: number;
  };
  intents?: string[];
  entities?: {
    persons: string[];
    organizations: string[];
    locations: string[];
    products: string[];
  };
  riskScore?: number;
  manipulationScore?: number;
  spamScore?: number;
  toxicityScore?: number;
  topics?: string[];
}

export const useEnhancedContentAnalysis = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const analyzeContentEnhanced = async (request: EnhancedAnalysisRequest): Promise<EnhancedAnalysisResult | null> => {
    if (!user) {
      setError('User not authenticated');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: functionError } = await supabase.functions.invoke('enhanced-content-analysis', {
        body: {
          ...request,
          user_id: user.id
        }
      });

      if (functionError) {
        throw new Error(functionError.message);
      }

      return data as EnhancedAnalysisResult;
    } catch (err: any) {
      setError(err.message || 'Failed to analyze content');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const batchAnalyzeEnhanced = async (
    requests: EnhancedAnalysisRequest[],
    onProgress?: (completed: number, total: number) => void
  ): Promise<EnhancedAnalysisResult[]> => {
    const results: EnhancedAnalysisResult[] = [];
    
    for (let i = 0; i < requests.length; i++) {
      const result = await analyzeContentEnhanced(requests[i]);
      if (result) {
        results.push(result);
      }
      onProgress?.(i + 1, requests.length);
    }

    return results;
  };

  return {
    analyzeContentEnhanced,
    batchAnalyzeEnhanced,
    loading,
    error
  };
};
